import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { JwtHelper } from '../../jwt-decoder/jwt-helper';

import { environment } from '../../../../environments/environment';
import { CartItemList } from '../../models/cart-model/cart-item-list.model';
import { CartItems } from '../../models/cart-model/cart-items.model';

@Injectable({
  providedIn: 'root'
})
export class GeneralService {

  jwtHelper: JwtHelper =  new JwtHelper();
  cartItemList: CartItemList = new CartItemList();

  constructor(private route: Router, private http: HttpClient) { }

  setToken(token: any) {
    localStorage.setItem('token', token);
  }

  getToken() {
    return localStorage.getItem('token')
  }

  isAuthenticated() {
    return this.getToken() !== null;
  }

  /**
   * Best-effort signout: clears the localStorage token and asks the
   * backend to invalidate the httpOnly JWT cookie. The redirect happens
   * regardless of the server response (the local token has already
   * been wiped).
   */
  logout() {
    localStorage.removeItem('token');
    const logoutUrl = environment.baseUrl.replace(/\/\d+\/?$/, '/') + 'logout';
    this.http.post(logoutUrl, {}, { withCredentials: true }).subscribe({
      next: () => this.route.navigate(['/login']),
      error: () => this.route.navigate(['/login']),
    });
  }

  decode() {
    const token = localStorage.getItem('token');
    if (token !== null) {
      return this.jwtHelper.decodeToken(token);
    }
  }

  decodeToken(token: any) {
    return this.jwtHelper.decodeToken(token);
  }

  setSelectedCity(cityObject: any) {
    localStorage.setItem('city', JSON.stringify(cityObject));
  }

  /**
   * Returns the customer's chosen delivery city. If nothing has been
   * stored yet (first visit, cleared localStorage), seed it with
   * Patna so the product/search/cart flows are not gated on the user
   * having opened the location picker first.
   *
   * Also self-heals: if the stored object is missing a `city_id` or
   * its id doesn't look like a 24-hex ObjectId, we overwrite it with
   * the default. An earlier version of the location-picker stored
   * the country document as the "city", which broke product filters
   * because the country _id isn't in any product's
   * `product_price.city` array.
   */
  getSelectedCity(): any {
    const defaultCity = {
      _id:       '61c0f17c8952dc068d5a1ee6',
      city_id:   '61c0f17c8952dc068d5a1ee6',
      city_name: 'Patna',
      city:      'Patna',
      post_code: '800006',
      pin_code:  '800006',
      state:     'Bihar',
    };

    const OBJECT_ID_RE = /^[a-f0-9]{24}$/i;
    const stored = this.safeParse(localStorage.getItem('city'));
    const id = stored?.city_id;
    const looksValid = typeof id === 'string' && OBJECT_ID_RE.test(id);

    if (stored && looksValid) {
      return stored;
    }

    // Stale shape or empty — overwrite.
    localStorage.setItem('city', JSON.stringify(defaultCity));
    return defaultCity;
  }

  addLocalCart(newCartItem: CartItems) {
    this.cartItemList.cartItems = this.getCartItems();
    this.findItemId(newCartItem);
    localStorage.setItem('cart_items', JSON.stringify(this.cartItemList.cartItems));
  }

  getCartItems(): any[] {
    return this.safeParse(localStorage.getItem('cart_items'));
  }

  private safeParse(raw: string | null): any {
    if (raw === null) { return null; }
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  findItemId(newCartItem: CartItems) {
    let itemIndex: number;
    if (this.cartItemList.cartItems && this.cartItemList.cartItems.length > 0) {
      itemIndex = this.cartItemList.cartItems.findIndex(item => item.product_id === newCartItem.product_id && item.unit === newCartItem.unit);
      if (itemIndex !== -1) {
        this.cartItemList.cartItems[itemIndex].quantity = newCartItem.quantity + this.cartItemList.cartItems[itemIndex].quantity;
      } else {
        this.cartItemList.cartItems.push(newCartItem);
      }
    } else {
      this.cartItemList.cartItems = [];
      this.cartItemList.cartItems.push(newCartItem);
    }
  }

  updateCartQuantity(cartItems: CartItems) {
    let itemIndex: number;
    if (this.cartItemList.cartItems && this.cartItemList.cartItems.length > 0) {
      itemIndex = this.cartItemList.cartItems.findIndex(item => item.product_id === cartItems.product_id && item.unit === cartItems.unit);
      if (itemIndex !== -1) {
        this.cartItemList.cartItems[itemIndex].quantity = +cartItems.quantity;
      }
    }
    
    localStorage.setItem('cart_items', JSON.stringify(this.cartItemList.cartItems));
  }

  deleteCartItems(cartObject: CartItems) {
    this.cartItemList.cartItems = this.getCartItems();
    if (this.cartItemList.cartItems && this.cartItemList.cartItems.length > 0) {
      const index = this.cartItemList.cartItems.findIndex(item => item.product_id === cartObject.product_id && item.unit === cartObject.unit)
      if (index !== -1) {
        this.cartItemList.cartItems.splice(index, 1);
      }

      localStorage.setItem('cart_items', JSON.stringify(this.cartItemList.cartItems));
    }
  }

  resetCartItem() {
    localStorage.removeItem('cart_items');
  }
  
}